import { Card } from '@/shared/components/ui/Card';
import { useToast } from '@/shared/components/ui/Toast';
import type { DashboardSettings } from '../services/dashboardSettingsService';

interface AlertNotificationPreference {
  alertType: string;
  emailOnYellow: boolean;
  emailOnRed: boolean;
  smsOnYellow: boolean;
  smsOnRed: boolean;
}

interface AlertNotificationSettingsProps {
  settings: DashboardSettings;
  locationId: string;
  hasPhoneNumber: boolean;
  preferences: AlertNotificationPreference[];
  onPreferenceChange: (alertType: string, field: string, value: boolean) => void;
}

const ALERT_CONFIGS = [
  {
    type: 'STALE_PENDING',
    label: 'Stale Pending Frames',
    description: 'Frames waiting to be ordered',
    color: 'yellow',
    enableField: 'enable_stale_pending',
    yellowField: 'stale_pending_yellow_days',
    redField: 'stale_pending_red_days',
  },
  {
    type: 'OVERDUE_ORDERED',
    label: 'Overdue Ordered Frames',
    description: 'Ordered frames not yet received',
    color: 'red',
    enableField: 'enable_overdue_ordered',
    yellowField: 'overdue_ordered_yellow_days',
    redField: 'overdue_ordered_red_days',
  },
  {
    type: 'LOW_USAGE',
    label: 'Low Usage Alert',
    description: 'Location usage below average',
    color: 'green',
    enableField: 'enable_usage_alert',
    yellowField: 'usage_yellow_pct',
    redField: 'usage_red_pct',
    isPercentage: true,
  },
];

export function AlertNotificationSettings({
  settings,
  hasPhoneNumber,
  preferences,
  onPreferenceChange,
}: AlertNotificationSettingsProps) {
  const { showToast } = useToast();

  const getPref = (alertType: string): AlertNotificationPreference => {
    return preferences.find(p => p.alertType === alertType) || {
      alertType,
      emailOnYellow: false,
      emailOnRed: true,
      smsOnYellow: false,
      smsOnRed: false,
    };
  };

  const handleSmsToggle = (alertType: string, field: string, value: boolean) => {
    if (value && !hasPhoneNumber) {
      showToast('Please add a phone number first to enable SMS notifications', 'error');
      return;
    }
    onPreferenceChange(alertType, field, value);
  };

  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-2">
        Alert Notifications
      </h3>
      <p className="text-sm text-gray-600 mb-6">
        Choose when to receive email and SMS notifications for each alert type.
        Notifications are sent daily at 8:00 AM (email) and 10:00 AM (SMS) Eastern Time.
      </p>

      <div className="space-y-6">
        {ALERT_CONFIGS.map((config) => {
          const isEnabled = settings[config.enableField as keyof DashboardSettings];
          const yellowValue = settings[config.yellowField as keyof DashboardSettings];
          const redValue = settings[config.redField as keyof DashboardSettings];
          const pref = getPref(config.type);
          const suffix = config.isPercentage ? '%' : ' days';

          return (
            <div
              key={config.type}
              className={`border-l-4 pl-4 py-3 ${
                config.color === 'yellow' ? 'border-yellow-500' :
                config.color === 'red' ? 'border-red-500' :
                'border-green-500'
              } ${!isEnabled ? 'opacity-50' : ''}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-gray-900">
                    {config.label}
                    {!isEnabled && (
                      <span className="ml-2 text-xs text-gray-500">(Dashboard alert disabled)</span>
                    )}
                  </h4>
                  <p className="text-xs text-gray-500 mt-1">
                    {config.description}
                  </p>
                  <div className="text-xs text-gray-600 mt-1">
                    <span className="inline-block bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded mr-2">
                      Yellow: {yellowValue}{suffix}
                    </span>
                    <span className="inline-block bg-red-100 text-red-800 px-2 py-0.5 rounded">
                      Red: {redValue}{suffix}
                    </span>
                  </div>
                </div>
              </div>

              {isEnabled && (
                <div className="mt-4 grid grid-cols-2 gap-4">
                  {/* Email notifications */}
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <span className="text-sm font-medium text-gray-700">Email</span>
                    </div>
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={pref.emailOnYellow}
                          onChange={(e) => onPreferenceChange(config.type, 'emailOnYellow', e.target.checked)}
                          className="rounded border-gray-300 text-yellow-600 focus:ring-yellow-500"
                        />
                        <span className="text-sm text-gray-600">Yellow threshold</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={pref.emailOnRed}
                          onChange={(e) => onPreferenceChange(config.type, 'emailOnRed', e.target.checked)}
                          className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                        />
                        <span className="text-sm text-gray-600">Red threshold</span>
                      </label>
                    </div>
                  </div>

                  {/* SMS notifications */}
                  <div className={`bg-gray-50 rounded-lg p-3 ${!hasPhoneNumber ? 'opacity-60' : ''}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      <span className="text-sm font-medium text-gray-700">SMS</span>
                      {!hasPhoneNumber && (
                        <span className="text-xs text-gray-500">(add phone first)</span>
                      )}
                    </div>
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={pref.smsOnYellow}
                          onChange={(e) => handleSmsToggle(config.type, 'smsOnYellow', e.target.checked)}
                          disabled={!hasPhoneNumber}
                          className="rounded border-gray-300 text-yellow-600 focus:ring-yellow-500 disabled:opacity-50"
                        />
                        <span className="text-sm text-gray-600">Yellow threshold</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={pref.smsOnRed}
                          onChange={(e) => handleSmsToggle(config.type, 'smsOnRed', e.target.checked)}
                          disabled={!hasPhoneNumber}
                          className="rounded border-gray-300 text-red-600 focus:ring-red-500 disabled:opacity-50"
                        />
                        <span className="text-sm text-gray-600">Red threshold</span>
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Info box */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex gap-3">
          <svg className="w-5 h-5 text-blue-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div className="text-sm text-blue-800">
            <p className="font-medium">How notifications work</p>
            <ul className="mt-1 list-disc list-inside text-blue-700 space-y-1">
              <li>All alerts for the day are combined into one email/SMS</li>
              <li>You'll receive notifications daily until items are resolved</li>
              <li>You can snooze specific items directly from the email</li>
            </ul>
          </div>
        </div>
      </div>
    </Card>
  );
}

